export const TREND_SOURCE_BADGES = {
  internal: {
    label: 'Platform Demand',
    className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  newsapi: {
    label: 'In the News',
    className: 'bg-sky-50 text-sky-800 border-sky-200',
  },
  google_trends: {
    label: 'Search Interest',
    className: 'bg-violet-50 text-violet-800 border-violet-200',
  },
  google_trends_related: {
    label: 'Rising Searches',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  google_trends_trending_now: {
    label: 'Trending Now',
    className: 'bg-red-50 text-red-800 border-red-200',
  },
};

/**
 * Parse top_trends / actionables whether they arrive as JSON strings or arrays.
 */
export const parseTrendJSON = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const SOURCE_KEY_ALIASES = {
  platform: 'internal',
  purchases: 'internal',
  internal: 'internal',
  news: 'newsapi',
  newsapi: 'newsapi',
  google_trends: 'google_trends',
  google_trends_related: 'google_trends_related',
  google_trends_trending_now: 'google_trends_trending_now',
};

/**
 * Infer data source badge from explicit source field or insight text heuristics.
 */
export const inferTrendSource = (trend) => {
  if (!trend) return null;

  const rawSource = (trend.source || '').toLowerCase().replace(/\s+/g, '_');
  const normalized = SOURCE_KEY_ALIASES[rawSource] || rawSource;
  if (TREND_SOURCE_BADGES[normalized]) {
    return { key: normalized, ...TREND_SOURCE_BADGES[normalized] };
  }

  const text = `${trend.insight || ''} ${trend.keyword || ''}`.toLowerCase();

  if (/trending now|real-time|breaking search|spiking search|google_trends_trending_now/.test(text)) {
    return { key: 'google_trends_trending_now', ...TREND_SOURCE_BADGES.google_trends_trending_now };
  }
  if (/rising search|breakout|related search|google_trends_related/.test(text)) {
    return { key: 'google_trends_related', ...TREND_SOURCE_BADGES.google_trends_related };
  }
  if (/search interest|google trend|search volume|search demand|serpapi/.test(text)) {
    return { key: 'google_trends', ...TREND_SOURCE_BADGES.google_trends };
  }
  if (/news|headline|media coverage|press|reported|newsapi/.test(text)) {
    return { key: 'newsapi', ...TREND_SOURCE_BADGES.newsapi };
  }
  if (/purchase|booking|subscription|enrollment|internal data|platform demand|your customers/.test(text)) {
    return { key: 'internal', ...TREND_SOURCE_BADGES.internal };
  }

  return null;
};

/**
 * Items Gemini flags as urgent / real-time (often from google_trends_trending_now).
 */
export const isTrendingNowItem = (trend) => {
  const source = inferTrendSource(trend);
  if (source?.key === 'google_trends_trending_now') return true;

  const text = (trend.insight || '').toLowerCase();
  return (
    /urgent|real-time|trending now|breaking|spiking|immediate action|act now/.test(text) ||
    ((trend.score || 0) >= 85 && /search|google trend/.test(text))
  );
};

const getServiceName = (service) => {
  if (typeof service === 'string') return service;
  return service?.name || service?.title || service?.service_name || '';
};

/**
 * Group trends by vendor service name when keywords match service names.
 */
export const groupTrendsByService = (trends, services) => {
  const serviceNames = (services || [])
    .map(getServiceName)
    .filter(Boolean);

  if (serviceNames.length < 2) {
    return { groups: null, ungrouped: trends };
  }

  const groups = {};
  const ungrouped = [];

  trends.forEach((trend) => {
    const keyword = (trend.keyword || '').toLowerCase();
    const match = serviceNames.find((name) => {
      const lower = name.toLowerCase();
      return lower && (keyword.includes(lower) || lower.includes(keyword));
    });

    if (match) {
      if (!groups[match]) groups[match] = [];
      groups[match].push(trend);
    } else {
      ungrouped.push(trend);
    }
  });

  const hasGroups = Object.keys(groups).length > 0;
  return {
    groups: hasGroups ? groups : null,
    ungrouped: hasGroups ? ungrouped : trends,
  };
};

export const getReportDate = (report) => {
  if (!report) return null;
  const raw = report.report_date || report.created_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getReportId = (report) => {
  if (!report) return null;
  const id = report.id ?? report.report_id;
  return id != null ? Number(id) : null;
};

export const normalizeSnapshotsPayload = (payload) => {
  if (!payload) return null;
  return payload.data ?? payload;
};

export const hasSnapshotCharts = (snapshots) => {
  if (!snapshots) return false;
  const services = snapshots.services;
  return Array.isArray(services) && services.length > 0;
};
